import { AnswerResponseProgress } from '@@types/answer.types';
import {
  AnswerAdditionalDataDto,
  AnswerAdditionalDataDtoTimeUnitEnum,
  AnswerSelectDto,
  QuestionSelectDto,
  SurveySnapshotSectionSelectDto,
  SurveyStatus,
} from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { AnswerAttachmentsInput } from '@components/pages/survey/components/answer-attachments-input.component';
import { ShortcutBadge } from '@components/pages/survey/components/question.component';
import { FormLabel } from '@components/shared/form-label.component';
import { Button } from '@components/ui/button';
import { CurrencyInput } from '@components/ui/currency-input';
import { Input } from '@components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Textarea } from '@components/ui/textarea.tsx';
import { errorToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { useInnovatorPatchAnswer } from '@queries/answer.query';
import { useGetSurvey } from '@queries/survey.query';
import { surveyAttachmentsStore, useSurveyAttachmentsStore } from '@stores/survey-attachments.store';
import { TIME_UNIT_OPTIONS } from '@utils/constants';
import { shouldIgnoreSurveyKeyboardShortcut } from '@utils/keyboard-shortcuts.util';
import { getQuestionIndexInSurvey, isQuestionAnswered } from '@utils/survey.util';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type QuestionTrlCharacteristicsComponentProps = {
  answers?: AnswerSelectDto[];
  handleNextQuestion: () => void;
  handlePreviousQuestion: () => void;
  previousQuestion?: QuestionSelectDto;
  question: QuestionSelectDto;
  section: SurveySnapshotSectionSelectDto;
  sectionId?: string;
  surveyId?: string;
};

type CurrencyValue = {
  amount: number;
  currency: string;
};

type FormState = {
  costEstimate: CurrencyValue | undefined;
  details: string;
  timeSpent: string;
  timeUnit?: AnswerAdditionalDataDtoTimeUnitEnum;
};

type FormErrors = {
  costEstimate?: string;
  details?: string;
  timeSpent?: string;
  timeUnit?: string;
};

const defaultFormState: FormState = {
  costEstimate: undefined,
  details: '',
  timeSpent: '',
  timeUnit: undefined,
};

const buildStateFromAnswer = (answer?: AnswerSelectDto): FormState => ({
  costEstimate:
    answer?.additionalData?.costEstimate !== undefined && answer.additionalData.costEstimate !== null
      ? { amount: answer.additionalData.costEstimate, currency: 'USD' }
      : undefined,
  details: answer?.details ?? '',
  timeSpent: answer?.additionalData?.timeSpent !== undefined ? String(answer.additionalData.timeSpent) : '',
  timeUnit: answer?.additionalData?.timeUnit ?? undefined,
});

const parseNumber = (value: string): number | undefined => {
  if (value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const wait = async (durationMs: number): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, durationMs));
};

export const QuestionTrlCharacteristicsComponent: React.FC<QuestionTrlCharacteristicsComponentProps> = (props) => {
  const {
    answers = [],
    handleNextQuestion,
    handlePreviousQuestion,
    previousQuestion,
    question,
    section,
    sectionId,
    surveyId,
  } = props;

  const answer = answers.find((item) => item.questionId === question.id && item.sectionId === sectionId);
  const questionAttachmentKey = surveyId && sectionId ? `${surveyId}:${sectionId}:${question.id}` : undefined;
  const [draftAnswerId, setDraftAnswerId] = useState<string | undefined>(answer?.id);
  const bufferedAttachmentCount = useSurveyAttachmentsStore((state) => {
    if (!questionAttachmentKey) {
      return 0;
    }

    return state.bufferedByQuestion[questionAttachmentKey]?.length ?? 0;
  });
  const resolvedAnswerId = answer?.id ?? draftAnswerId;
  const hasPendingBufferWithoutAnswer = Boolean(!resolvedAnswerId && bufferedAttachmentCount > 0);
  const { data: survey } = useGetSurvey(surveyId);
  const { mutateAsync: patchAnswer } = useInnovatorPatchAnswer();

  const [formState, setFormState] = useState<FormState>(() => buildStateFromAnswer(answer));
  const [errors, setErrors] = useState<FormErrors>({});
  const [pendingAnswer, setPendingAnswer] = useState<Promise<AnswerSelectDto> | null>(null);
  const [isNextClicked, setIsNextClicked] = useState(false);
  const [isNotApplicableClicked, setIsNotApplicableClicked] = useState(false);
  const [isPreviousClicked, setIsPreviousClicked] = useState(false);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const pendingAnswerRef = useRef(pendingAnswer);
  const isDirtyRef = useRef(isDirty);
  const hasFormDataRef = useRef(false);
  const answerIsCompleteRef = useRef(false);

  useEffect(() => {
    setFormState(buildStateFromAnswer(answer));
    setErrors({});
    setIsDirty(false);
  }, [answer]);

  useEffect(() => {
    setDraftAnswerId(answer?.id);
  }, [answer?.id]);

  const disableInputs = survey?.status !== SurveyStatus.DRAFT;
  const isNotApplicable = answer?.response === AnswerResponseProgress.NOT_APPLICABLE;
  const hasFormData = Boolean(
    formState.details.trim() ||
      formState.costEstimate !== undefined ||
      formState.timeSpent !== '' ||
      formState.timeUnit,
  );
  const answerIsComplete =
    sectionId && survey?.snapshot
      ? isQuestionAnswered({
          answers,
          questionId: question.id,
          sectionId,
          questionType: question.type,
        })
      : false;

  const globalIndexInSurvey = useMemo(() => {
    return getQuestionIndexInSurvey({
      questionId: question.id,
      sectionId,
      snapshot: survey?.snapshot,
    });
  }, [question.id, sectionId, survey?.snapshot]);

  // Keep refs in sync
  useEffect(() => {
    pendingAnswerRef.current = pendingAnswer;
  }, [pendingAnswer]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    hasFormDataRef.current = hasFormData;
  }, [hasFormData]);

  useEffect(() => {
    answerIsCompleteRef.current = answerIsComplete;
  }, [answerIsComplete]);

  const handleFieldChange = <Key extends keyof FormState>(field: Key, value: FormState[Key]) => {
    if (disableInputs) return;
    setFormState((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleCostEstimateChange = (value: CurrencyValue | undefined) => {
    if (disableInputs) return;
    setFormState((prev) => ({ ...prev, costEstimate: value }));
    setIsDirty(true);
  };

  const validateForm = useCallback((): boolean => {
    const nextErrors: FormErrors = {};

    if (!formState.details.trim()) {
      nextErrors.details = 'Please provide a detailed response.';
    }
    if (formState.timeSpent === '') {
      nextErrors.timeSpent = 'Please enter the time spent.';
    }
    if (!formState.timeUnit) {
      nextErrors.timeUnit = 'Please select a time unit.';
    }
    const costAmount = formState.costEstimate?.amount;
    if (costAmount === undefined || costAmount === 0 || !Number.isFinite(costAmount)) {
      nextErrors.costEstimate = 'Please enter a cost estimate.';
    }

    setErrors(nextErrors);
    const isValid = Object.keys(nextErrors).length === 0;

    if (!isValid) {
      errorToast({ description: 'Please fill out all required fields.' });
    }

    return isValid;
  }, [formState.costEstimate, formState.details, formState.timeSpent, formState.timeUnit]);

  const buildPayload = useCallback(
    (options?: { clear?: boolean }): AnswerAdditionalDataDto | undefined => {
      if (options?.clear) {
        return undefined;
      }

      // Treat 0 as undefined since it means "empty" when emptyValue="zero"
      const costEstimate =
        formState.costEstimate?.amount !== undefined && formState.costEstimate.amount !== 0
          ? formState.costEstimate.amount
          : undefined;
      const timeSpent = parseNumber(formState.timeSpent);

      if (costEstimate === undefined && timeSpent === undefined && !formState.timeUnit) {
        return undefined;
      }

      return {
        costEstimate,
        timeSpent,
        timeUnit: formState.timeUnit,
      };
    },
    [formState.costEstimate, formState.timeSpent, formState.timeUnit],
  );

  const saveAnswer = useCallback(
    async (options?: { clear?: boolean }): Promise<boolean> => {
      // Skip saving if survey is not in draft state
      if (disableInputs) return true;

      if (!sectionId || !surveyId) return false;

      if (options?.clear) {
        setErrors({});
        const answerPromise = patchAnswer({
          additionalData: undefined,
          details: '',
          questionId: question.id,
          sectionId,
          surveyId,
        });
        setPendingAnswer(answerPromise);
        try {
          const savedAnswer = await answerPromise;
          setDraftAnswerId(savedAnswer.id);
          setIsDirty(false);
          return true;
        } catch {
          errorToast({ description: 'Failed to clear answer. Please try again.' });
          return false;
        } finally {
          setPendingAnswer(null);
        }
      }

      if (!hasFormDataRef.current) {
        if (hasPendingBufferWithoutAnswer) {
          return validateForm();
        }

        return true;
      }

      if (!validateForm()) {
        return false;
      }

      const additionalData = buildPayload();
      const answerPromise = patchAnswer({
        additionalData,
        details: formState.details,
        questionId: question.id,
        sectionId,
        surveyId,
      });
      setPendingAnswer(answerPromise);
      try {
        const savedAnswer = await answerPromise;
        setDraftAnswerId(savedAnswer.id);
        setIsDirty(false);
        return true;
      } catch {
        errorToast({ description: 'Failed to save answer. Please try again.' });
        return false;
      } finally {
        setPendingAnswer(null);
      }
    },
    [
      buildPayload,
      disableInputs,
      formState.details,
      hasPendingBufferWithoutAnswer,
      patchAnswer,
      question.id,
      sectionId,
      surveyId,
      validateForm,
    ],
  );

  const waitForBufferedAttachmentsToSync = useCallback(async (): Promise<boolean> => {
    if (!questionAttachmentKey) {
      return true;
    }

    const maxWaitDurationMs = 8000;
    const pollIntervalMs = 100;
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitDurationMs) {
      const bufferedAttachments = surveyAttachmentsStore.getState().getBufferedAttachments(questionAttachmentKey);
      if (bufferedAttachments.length === 0) {
        return true;
      }

      await wait(pollIntervalMs);
    }

    return surveyAttachmentsStore.getState().getBufferedAttachments(questionAttachmentKey).length === 0;
  }, [questionAttachmentKey]);

  const handleNext = useCallback(async () => {
    setIsNextClicked(true);
    try {
      let hasSavedAnswerForBufferedAttachments = false;

      if (hasPendingBufferWithoutAnswer) {
        const saved = await saveAnswer();
        if (!saved) {
          return;
        }
        hasSavedAnswerForBufferedAttachments = true;

        const synced = await waitForBufferedAttachmentsToSync();
        if (!synced) {
          errorToast({ description: 'Attachments are still uploading. Please try again in a moment.' });
          return;
        }
      }

      if (pendingAnswerRef.current) {
        await pendingAnswerRef.current;
        setPendingAnswer(null);
      }

      const shouldClear = !hasFormDataRef.current && isDirtyRef.current && answerIsCompleteRef.current;
      if (shouldClear) {
        await saveAnswer({ clear: true });
      } else if (hasFormDataRef.current && !hasSavedAnswerForBufferedAttachments) {
        const saved = await saveAnswer();
        if (!saved) {
          return;
        }
      }

      handleNextQuestion();
    } finally {
      setIsNextClicked(false);
    }
  }, [handleNextQuestion, hasPendingBufferWithoutAnswer, saveAnswer, waitForBufferedAttachmentsToSync]);

  const handlePrevious = useCallback(async () => {
    if (hasPendingBufferWithoutAnswer) {
      errorToast({ description: 'Answer should be submitted in order to save attachment.' });
      return;
    }

    setIsPreviousClicked(true);
    if (pendingAnswerRef.current) {
      await pendingAnswerRef.current;
      setPendingAnswer(null);
    }
    handlePreviousQuestion();
    setIsPreviousClicked(false);
  }, [handlePreviousQuestion, hasPendingBufferWithoutAnswer]);

  const handleNotApplicable = useCallback(async () => {
    if (disableInputs) return;
    if (!sectionId || !surveyId) return;

    setIsNotApplicableClicked(true);

    if (pendingAnswerRef.current) {
      await pendingAnswerRef.current;
      setPendingAnswer(null);
    }

    setErrors({});
    const answerPromise = patchAnswer({
      additionalData: undefined,
      details: '',
      questionId: question.id,
      response: AnswerResponseProgress.NOT_APPLICABLE,
      sectionId,
      surveyId,
    });
    setPendingAnswer(answerPromise);

    try {
      await answerPromise;
      setFormState(defaultFormState);
      setIsDirty(false);
      handleNextQuestion();
    } catch {
      errorToast({ description: 'Failed to mark as not applicable. Please try again.' });
    } finally {
      setPendingAnswer(null);
      setIsNotApplicableClicked(false);
    }
  }, [disableInputs, handleNextQuestion, patchAnswer, question.id, sectionId, surveyId]);

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger on key repeat (when key is held down)
      if (isAttachmentModalOpen || event.repeat || shouldIgnoreSurveyKeyboardShortcut(event)) return;

      // Arrow key navigation
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
        return;
      }

      if (event.key === 'ArrowLeft' && previousQuestion) {
        event.preventDefault();
        handlePrevious();
        return;
      }

      // "N" for Not Applicable
      if (event.key === 'n' || event.key === 'N') {
        event.preventDefault();
        handleNotApplicable();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handleNotApplicable, handlePrevious, isAttachmentModalOpen, previousQuestion]);

  return (
    <div className="@container/trl-question mx-auto w-full max-w-[800px] px-3 pb-6 pt-5">
      <div className="flex items-center gap-3">
        <div className="text-[#3d2eff] font-medium">
          Question {globalIndexInSurvey + 1} {question.label && `– ${question.label}`}
        </div>
      </div>
      <div className="text-stone-900/70 pt-3">{question.prompt}</div>

      {question.description && (
        <div className="text-stone-900/70 px-3 py-1.5 rounded-lg mt-4 border border-blue-500/40 text-xs bg-blue-50">
          <span className="font-semibold">Why it matters for Tyr?</span> {question.description}
        </div>
      )}

      <div className="flex flex-col gap-6 mt-6">
        <div className="flex flex-col items-start gap-1.5">
          <div className="flex items-center gap-2">
            <FormLabel
              className={cn(errors.details && 'text-red-500')}
              label="Detailed Response"
              htmlFor="details"
            />
            <TooltipComponent side="bottom">
              Provide as much relevant information as possible. Do not include sensitive intellectual property or
              classified information.
            </TooltipComponent>
          </div>
          <Textarea
            className="w-full"
            disabled={disableInputs}
            hasError={Boolean(errors.details)}
            id="details"
            name="details"
            onChange={(event) => handleFieldChange('details', event.target.value)}
            placeholder="Provide a detailed response about your technology..."
            value={formState.details}
          />
          {errors.details && <p className="text-xs text-red-500">{errors.details}</p>}
        </div>

        <div className="grid grid-cols-1 gap-4 @min-[44rem]/trl-question:grid-cols-2 @min-[64rem]/trl-question:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <FormLabel
              className={cn(errors.timeSpent && 'text-red-500')}
              label="Time Spent"
              htmlFor="timeSpent"
            />
            <Input
              disabled={disableInputs}
              hasError={Boolean(errors.timeSpent)}
              id="timeSpent"
              min="0"
              onChange={(event) => handleFieldChange('timeSpent', event.target.value)}
              placeholder="e.g., 5"
              step="any"
              type="number"
              value={formState.timeSpent}
            />
            {errors.timeSpent && <p className="text-xs text-red-500">{errors.timeSpent}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <FormLabel
              className={cn(errors.timeUnit && 'text-red-500')}
              label="Time Unit"
            />
            <Select
              value={formState.timeUnit}
              onValueChange={(value: AnswerAdditionalDataDtoTimeUnitEnum) => handleFieldChange('timeUnit', value)}
              disabled={disableInputs}
            >
              <SelectTrigger
                aria-label="Select time unit"
                hasError={Boolean(errors.timeUnit)}
              >
                <SelectValue placeholder="Select unit" />
              </SelectTrigger>
              <SelectContent>
                {TIME_UNIT_OPTIONS.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.timeUnit && <p className="text-xs text-red-500">{errors.timeUnit}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <FormLabel
              className={cn(errors.costEstimate && 'text-red-500')}
              label="Cost Estimate (USD)"
              htmlFor="costEstimate"
            />
            <CurrencyInput
              className={cn(errors.costEstimate && 'border-red-500')}
              currency="USD"
              disabled={disableInputs}
              emptyValue="zero"
              onChange={handleCostEstimateChange}
              placeholder="0.00"
              value={formState.costEstimate}
            />
            {errors.costEstimate && <p className="text-xs text-red-500">{errors.costEstimate}</p>}
          </div>
        </div>

        <AnswerAttachmentsInput
          answerId={resolvedAnswerId}
          canEdit={!disableInputs}
          onModalOpenChange={setIsAttachmentModalOpen}
          questionAttachmentKey={questionAttachmentKey}
        />
      </div>

      <div className="@container/trl-actions mt-8">
        <div className="flex w-full flex-col gap-3 pt-4 @min-[36rem]/trl-actions:flex-row @min-[36rem]/trl-actions:justify-end @min-[36rem]/trl-actions:gap-4">
          <div className="w-full @min-[36rem]/trl-actions:min-w-[140px] @min-[36rem]/trl-actions:w-auto">
            <Button
              className="w-full"
              disabled={!previousQuestion || isPreviousClicked || isNextClicked || isNotApplicableClicked}
              onClick={handlePrevious}
              variant="secondary"
            >
              <span className="flex items-center justify-center gap-2">
                Previous
                <ShortcutBadge
                  className="underline text-gray-500 border-gray-400"
                  shortcut="←"
                  tooltipText="Press ← (Left Arrow) to go to previous question"
                />
              </span>
            </Button>
          </div>
          <div className="w-full @min-[36rem]/trl-actions:min-w-[140px] @min-[36rem]/trl-actions:w-auto">
            <Button
              className="w-full"
              disabled={disableInputs || isNextClicked || isPreviousClicked || isNotApplicableClicked}
              onClick={handleNotApplicable}
              variant={isNotApplicable ? 'primary' : 'secondary'}
            >
              <span className="flex items-center justify-center gap-2">
                Not applicable
                <ShortcutBadge
                  className="underline text-gray-500 border-gray-400"
                  shortcut="N"
                  tooltipText="Press N to mark as Not Applicable"
                />
              </span>
            </Button>
          </div>
          <div className="w-full @min-[36rem]/trl-actions:min-w-[140px] @min-[36rem]/trl-actions:w-auto">
            <Button
              className="w-full"
              disabled={isNextClicked || isPreviousClicked || isNotApplicableClicked}
              onClick={handleNext}
              variant="primary"
            >
              <span className="flex items-center justify-center gap-2">
                Next
                <ShortcutBadge
                  className="underline"
                  shortcut="→"
                  tooltipText="Press → (Right Arrow) to go to next question"
                />
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
